import { useQueryClient } from '@tanstack/react-query'
import { posthog } from 'posthog-js'
import { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'

import { ProductLoadingPage, useProductShell } from '@/components/sidebar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { PageSpinner } from '@/components/ui/PageSpinner'
import { AppLoadError } from '../../components/AppLoadError'
import { BrokerWidget } from '../../components/Broker/BrokerWidget'
import { SignInGate } from '../../components/SignIn/SignInGate'
import { isDevelopment } from '../../config/config'
import { identifyOrg } from '../../lib/analytics'
import { CacheKeys } from '../../lib/cache'
import { useHostGuard } from '../../lib/hostGuard'
import { useAuthStatus, useSignOut } from '../../models/auth'
import { getCurrentUser, hasOrg, OrgUserStatus, useCurrentUser } from '../../models/user'
import { InsuranceOffer } from './App/InsuranceOffer'
import { showsBrokerWidget } from './brokerWidgetRoutes'

// The prefetch and the layout's own query share it, so a user fetched alongside sign-in isn't fetched again on mount.
const PREFETCHED_USER_STALE_MS = 5_000

function DeactivatedMessage() {
  const signOut = useSignOut()

  return (
    <div className="flex min-h-screen items-center justify-center">
      <Card className="max-w-md">
        <CardHeader>
          <CardTitle>Account Deactivated</CardTitle>
          <CardDescription>
            Your account has been deactivated by an administrator. You no longer have access to this
            organization.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            If you believe this is a mistake, please contact your organization administrator.
          </p>
        </CardContent>
        <CardFooter>
          <Button
            loading={signOut.isPending || signOut.isSuccess ? 'Signing out…' : undefined}
            onClick={() => signOut.mutate()}
          >
            Sign out
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}

function NoOrgMessage() {
  const signOut = useSignOut()

  return (
    <div className="flex min-h-screen items-center justify-center">
      <Card className="max-w-md">
        <CardHeader>
          <CardTitle>No organization</CardTitle>
          <CardDescription>This account isn&apos;t linked to an organization.</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">Ask a Cai admin to add it to one.</p>
        </CardContent>
        <CardFooter>
          <Button
            loading={signOut.isPending || signOut.isSuccess ? 'Signing out…' : undefined}
            onClick={() => signOut.mutate()}
          >
            Sign out
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}

function pageLoadingLabel(pathname: string): string | undefined {
  return pathname.startsWith('/quotes') ? 'Loading quotes…' : undefined
}

export function PrivateLayout() {
  // In prod/staging the app belongs on the app host; if an app route is loaded
  // on the marketing host, bounce there (no-op in dev/preview).
  const { redirecting } = useHostGuard('app')
  const shell = useProductShell()
  const { data: authStatus } = useAuthStatus()
  const signedInHere = !!shell && !shell.signedIn && !!authStatus?.authenticated
  const onShellSignIn = shell?.onSignedIn
  // Insure's email-code sign-in finishes without a page load or a navigation; the shell must hear of it.
  useEffect(() => {
    if (signedInHere) onShellSignIn?.()
  }, [signedInHere, onShellSignIn])
  const queryClient = useQueryClient()
  const loadingLabel = pageLoadingLabel(useLocation().pathname)

  // Asked alongside the sign-in check rather than after it, so the page waits one round trip instead of two.
  useEffect(() => {
    if (redirecting) return
    void queryClient.prefetchQuery({
      queryKey: [CacheKeys.CURRENT_USER],
      queryFn: getCurrentUser,
      staleTime: PREFETCHED_USER_STALE_MS,
    })
  }, [queryClient, redirecting])

  if (redirecting) return null

  return (
    <SignInGate
      loadingLabel={loadingLabel}
      // In the shared shell a session without an Insure user is a fleet without cover yet: show the offer.
      withoutAccount={
        shell?.signedIn ? (
          <main className="flex min-h-full flex-col p-4 md:p-8">
            <InsuranceOffer />
          </main>
        ) : undefined
      }
    >
      <PrivateLayoutContainer loadingLabel={loadingLabel} />
    </SignInGate>
  )
}

function PrivateLayoutContainer({ loadingLabel }: { loadingLabel?: string }) {
  const shell = useProductShell()
  const { user, isLoading, isError, error, isRefetching, refetch } = useCurrentUser({
    enabled: true,
    staleTime: PREFETCHED_USER_STALE_MS,
  })
  const location = useLocation()

  useEffect(() => {
    if (isDevelopment || !user) return

    posthog.identify(user.customUserId || user.id, {
      mobile: user.mobile,
      isCaiAdmin: user.isCaiAdmin,
      email: user.email,
      name: user.firstName + ' ' + user.lastName,
      org_id: hasOrg(user) ? user.currentOrg.id : null,
    })
    // re-runs on org switch, so later events are not attributed to the old org
    identifyOrg(hasOrg(user) ? user.currentOrg : null)
  }, [user])

  useEffect(() => {
    if (isDevelopment || !user) return
    const trackedKey = `posthog_sign_in_tracked_${user.customUserId}`
    if (sessionStorage.getItem(trackedKey)) return

    posthog.capture('insure_user_signed_in', {
      email: user.email,
      name: user.firstName + ' ' + user.lastName,
      org_id: user.currentOrg?.id,
      org_name: user.currentOrg?.name,
      is_cai_admin: user.isCaiAdmin,
      role: user.orgUser?.role,
    })
    sessionStorage.setItem(trackedKey, '1')
  }, [user])

  useEffect(() => {
    if (isDevelopment || !user) return

    posthog.capture('insure_user_active')
    const interval = setInterval(() => posthog.capture('insure_user_active'), 5 * 60 * 1000)
    return () => clearInterval(interval)
    // depend on user.id not the full object — avoids re-triggering on refetches
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  // Only a failed cold start errors; a cached user survives failed refetches.
  if (isError && !user) {
    return <AppLoadError error={error} onRetry={() => void refetch()} retrying={isRefetching} />
  }

  if (isLoading || !user) {
    // In the shared shell the bar and rail are already up, so the page loads as a skeleton beside them.
    return shell ? <ProductLoadingPage /> : <PageSpinner label={loadingLabel} />
  }

  if (user.orgUser?.status === OrgUserStatus.DEACTIVATED) {
    return <DeactivatedMessage />
  }

  const isOnAcceptPage = location.pathname.startsWith('/insure/accept-invitation/')
  if (user.pendingInvitation && !isOnAcceptPage) {
    return <Navigate to={`/insure/accept-invitation/${user.pendingInvitation.id}`} replace />
  }

  // Every page below assumes an org, so an account without one stops here.
  if (!hasOrg(user) && !isOnAcceptPage) {
    return <NoOrgMessage />
  }

  return (
    <>
      <Outlet />
      {showsBrokerWidget(location.pathname) && <BrokerWidget />}
    </>
  )
}
